// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Haggle — turning a finished deal into the thing both players actually want to read.
//
// Principle #9: the summary shows EVERY player's outcome, broken down by what they did — not a name
// and a number. Here that means, per player and per ware, how many cards they sold and what those
// sales actually paid, so you can see the exact moment the other trader got ahead of you on Copper.
//
// It also shows WHAT EVERYONE MISSED, which this game can state exactly because the printed value of
// a deal is a known constant: 91 chits on the tariffs plus 38 in bounties. Whatever neither player
// banked was either burned by the bell or left standing on a column nobody drained — and unsold
// cards in a satchel at the end are worth precisely nothing, which is the lesson the game is
// teaching.

import { BOUNTY_TOTAL, TARIFF_TOTAL, WARES, WARE_NAME, type Ware } from './wares';
import { other, scoreOf, sum, type PublicState, type Score, type Seat } from './rules';

export interface WareLine {
  readonly ware: Ware;
  readonly cards: number;
  readonly chits: number;
  /** The best single sale they made of this ware, for the "they got the 8" read. */
  readonly best: number;
}

export interface PlayerLine {
  readonly seat: Seat;
  readonly name: string;
  readonly score: Score;
  readonly gang: number;
  /** Ware cards still in the satchel when the deal ended. Worth nothing. */
  readonly stranded: number;
  readonly sales: WareLine[];
  readonly bounties: number;
  readonly won: boolean;
}

export interface Summary {
  readonly lines: [PlayerLine, PlayerLine];
  readonly winner: Seat;
  readonly reason: string;
  /** Printed value nobody banked: burned by the bell, or left on a column. */
  readonly unclaimed: number;
  readonly burned: number;
  readonly plies: number;
}

const REASONS: Record<string, string> = {
  dry: 'Three tariffs ran dry.',
  exhausted: 'The crate ran out and the trestle was bare.',
  cap: 'The deal ran long and was called.',
  walkover: 'Your rival left the room.',
};

export function summarise(
  p: PublicState,
  satchels: readonly [readonly Ware[], readonly Ware[]],
  names: readonly string[],
  winner: Seat,
): Summary {
  const lines: PlayerLine[] = [];
  for (const seat of [0, 1] as Seat[]) {
    const per = new Map<Ware, { cards: number; chits: number; best: number }>();
    let bounties = 0;
    for (const ev of p.log) {
      if (ev.k !== 'sell' || ev.seat !== seat) continue;
      const cur = per.get(ev.ware) ?? { cards: 0, chits: 0, best: 0 };
      const paid = sum(ev.chits);
      cur.cards += ev.cards;
      cur.chits += paid;
      cur.best = Math.max(cur.best, paid);
      per.set(ev.ware, cur);
      if (ev.bounty > 0) bounties++;
    }
    const sales: WareLine[] = WARES.filter((w) => per.has(w)).map((w) => {
      const v = per.get(w)!;
      return { ware: w, cards: v.cards, chits: v.chits, best: v.best };
    });
    lines.push({
      seat,
      name: names[seat] ?? `Trader ${seat + 1}`,
      score: scoreOf(p, seat),
      gang: p.gang[seat],
      stranded: satchels[seat].length,
      sales,
      bounties,
      won: seat === winner,
    });
  }
  const banked = lines[0].score.chits + lines[1].score.chits + lines[0].score.bounty + lines[1].score.bounty;
  return {
    lines: [lines[0], lines[1]],
    winner,
    reason: REASONS[p.endReason ?? ''] ?? 'The deal ended.',
    unclaimed: TARIFF_TOTAL + BOUNTY_TOTAL - banked,
    burned: p.burned,
    plies: p.ply,
  };
}

export const wareName = (w: Ware): string => WARE_NAME[w];

/** A one-line, provable read of the rival's opening deal — the exact inference the bot makes. */
export function provableRead(p: PublicState, seat: Seat): string | null {
  const rival = other(seat);
  const took = new Map<Ware, number>();
  const spent = new Map<Ware, number>();
  const bump = (m: Map<Ware, number>, w: Ware, n = 1): void => {
    m.set(w, (m.get(w) ?? 0) + n);
  };
  for (const ev of p.log) {
    if (ev.k === 'take' && ev.seat === rival) bump(took, ev.ware);
    else if (ev.k === 'barter' && ev.seat === rival) {
      for (const w of ev.took) bump(took, w);
      for (const w of ev.gaveWares) bump(spent, w);
    } else if (ev.k === 'sell' && ev.seat === rival) bump(spent, ev.ware, ev.cards);
    else if (ev.k === 'dump' && ev.seat === rival && ev.card !== 'porter') bump(spent, ev.card);
  }
  let best: { ware: Ware; n: number } | null = null;
  for (const w of WARES) {
    const n = (spent.get(w) ?? 0) - (took.get(w) ?? 0);
    if (n > 0 && (!best || n > best.n)) best = { ware: w, n };
  }
  if (!best) return null;
  return `They were dealt at least ${best.n} ${wareName(best.ware)}.`;
}
